import {
  Add,
  Close,
  DeleteForever,
  DeleteOutline,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  PersonAddAlt1,
  PlayArrowOutlined,
} from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Link as MuiLink,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { fillRouteWithSlugs } from "@utils";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "src/components";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { StatCard } from "src/components/ui/StatCard";
import { getTrainingRevisionLabel } from "src/components/Trainings/revision-label";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import {
  getFallbackDeviceId,
  useGetDevices,
  useGetFunctionalGroups,
  useGetUser,
  useGetUsersByOrg,
  useGlobalDeviceContext,
} from "src/hooks";
import {
  useAddOccurrenceTrainee,
  useDeleteOccurrenceTask,
  useDeleteTaskProgram,
  useDeleteTaskProgramOccurrence,
  useGetTaskProgramDetail,
  useTriggerTaskProgramForRevision,
  useUpdateTaskProgram,
} from "src/hooks/queries/task-program-mutations";
import { usePublishTrainingQuiz } from "src/hooks/queries/training-quiz";
import {
  TaskProgram,
  TaskProgramAssignmentInput,
  TaskProgramExpectedUser,
  TaskProgramOccurrenceDetail,
} from "src/services/backend";
import { EntitlementType } from "src/stores/models";
import { hasFunctionalGroupEntitlement } from "src/utils/entitlements";
import { getUserDisplayName } from "src/utils/user";

type TrainingTask = TaskProgramOccurrenceDetail["tasks"][number];

const STATUS_STYLE: Record<
  TrainingTask["manualStatus"],
  {
    label: string;
    bg: string;
    fg: string;
    dot: string;
  }
> = {
  TODO: {
    label: "Pending",
    bg: "#FEF3C7",
    fg: "#B45309",
    dot: "#F59E0B",
  },
  IN_PROGRESS: {
    label: "In progress",
    bg: "#DBEAFE",
    fg: "#1D4ED8",
    dot: "#3B82F6",
  },
  DONE: {
    label: "Completed",
    bg: "#D1FAE5",
    fg: "#047857",
    dot: "#10B981",
  },
};

const initialsFromUser = (u: {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}): string => {
  const f = (u.firstName ?? "").trim();
  const l = (u.lastName ?? "").trim();
  if (f || l) return `${f[0] ?? ""}${l[0] ?? ""}`.toUpperCase();
  return (u.email[0] ?? "?").toUpperCase();
};

const colorFromString = (s: string): string => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue}, 70%, 92%)`;
};

const fgColorFromBg = (s: string): string => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue}, 60%, 35%)`;
};

const SELECTED_AUTOCOMPLETE_SX = {
  "&& .MuiAutocomplete-inputRoot": {
    paddingTop: "14px",
    paddingBottom: "6px",
  },
  "&& .MuiAutocomplete-tag": {
    marginTop: "4px",
  },
};

const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const occurrenceTitle = (occ: TaskProgramOccurrenceDetail): string => {
  const revision = occ.documentVersion?.revision;
  const revisionLabel =
    revision != null ? getTrainingRevisionLabel(revision) : null;
  return revisionLabel ?? `Revision — ${formatDate(occ.triggeredAt)}`;
};

const triggererLabel = (occ: TaskProgramOccurrenceDetail): string => {
  if (occ.triggeredByUser) return getUserDisplayName(occ.triggeredByUser);
  return "System";
};

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const TrainingDetail = () => {
  const { orgId = "", programId = "" } = useParams();
  const navigate = useNavigate();
  const { data: user } = useGetUser();
  const { activeGlobalDeviceId } = useGlobalDeviceContext();
  const { data: devices = [] } = useGetDevices({ orgId });
  const canManage =
    !!user?.permissions?.includes("write:configuration") ||
    (!!user?.organizationMemberships &&
      hasFunctionalGroupEntitlement(
        user,
        orgId,
        EntitlementType.ADMINISTER_ALL_TRAININGS
      ));

  const { data, isLoading, isError } = useGetTaskProgramDetail(
    orgId,
    programId
  );
  const triggerForRevisionMutation = useTriggerTaskProgramForRevision({
    orgId,
  });
  const publishQuizMutation = usePublishTrainingQuiz({ orgId });
  const deleteMutation = useDeleteTaskProgram({ orgId });
  const deleteOccurrenceMutation = useDeleteTaskProgramOccurrence({ orgId });
  const deleteOccurrenceTaskMutation = useDeleteOccurrenceTask({ orgId });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [revisionPickerOpen, setRevisionPickerOpen] = useState(false);
  const [pickedRevisionId, setPickedRevisionId] = useState<string | null>(null);
  const [confirmDeleteOccurrence, setConfirmDeleteOccurrence] =
    useState<TaskProgramOccurrenceDetail | null>(null);
  const [confirmDeleteTask, setConfirmDeleteTask] = useState<{
    occurrenceId: string;
    task: TrainingTask;
  } | null>(null);
  const [poolEditOpen, setPoolEditOpen] = useState(false);
  const [addTraineeOccurrence, setAddTraineeOccurrence] =
    useState<TaskProgramOccurrenceDetail | null>(null);

  const trainingsHref = fillRouteWithSlugs(ROUTES.TRAININGS, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
  });
  const trainingEditHref = fillRouteWithSlugs(ROUTES.TRAINING_EDIT, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
    [ROUTE_SLUGS.PROGRAM_ID]: programId,
  });
  const trainingQuizEditHref = fillRouteWithSlugs(ROUTES.TRAINING_QUIZ_EDIT, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
    [ROUTE_SLUGS.PROGRAM_ID]: programId,
  });

  const sortedOccurrences = useMemo<TaskProgramOccurrenceDetail[]>(() => {
    if (!data || !Array.isArray(data.occurrences)) return [];
    return [...data.occurrences].sort(
      (a, b) =>
        new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime()
    );
  }, [data]);

  const sortedTasksByOccurrenceId = useMemo<
    Record<string, TaskProgramOccurrenceDetail["tasks"]>
  >(() => {
    const result: Record<string, TaskProgramOccurrenceDetail["tasks"]> = {};
    for (const occ of sortedOccurrences) {
      result[occ.id] = [...occ.tasks].sort((a, b) => {
        const al = a.assignee ? getUserDisplayName(a.assignee) : "";
        const bl = b.assignee ? getUserDisplayName(b.assignee) : "";
        return al.localeCompare(bl);
      });
    }
    return result;
  }, [sortedOccurrences]);

  const overviewStats = useMemo(() => {
    let total = 0;
    let completed = 0;
    if (!data || !Array.isArray(data.occurrences)) {
      return { total, completed, outstanding: 0, pct: 0 };
    }
    for (const occ of data?.occurrences ?? []) {
      for (const task of occ.tasks) {
        total += 1;
        if (task.manualStatus === "DONE") completed += 1;
      }
    }
    const outstanding = Math.max(total - completed, 0);
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, outstanding, pct };
  }, [data]);

  const [expandedById, setExpandedById] = useState<Record<string, boolean>>({});

  const isExpanded = (occId: string, idx: number): boolean => {
    if (occId in expandedById) return expandedById[occId];
    return idx === 0;
  };

  const toggleExpanded = (occId: string, idx: number) => {
    setExpandedById((prev) => ({
      ...prev,
      [occId]: !(occId in prev ? prev[occId] : idx === 0),
    }));
  };

  if (isError || (!!data && !Array.isArray(data.occurrences))) {
    return (
      <Box className="flex flex-col gap-4">
        <PageHeader
          pageTitleComponent={
            <PageBreadcrumb
              items={[
                { label: "Training Programs", to: trainingsHref },
                { label: "…" },
              ]}
            />
          }
        />
        <Box className="flex flex-1 flex-col items-center justify-center gap-2 py-8 px-3">
          <Typography color="error">Training program not found</Typography>
          <Button onClick={() => navigate(trainingsHref)}>
            Back to Trainings
          </Button>
        </Box>
      </Box>
    );
  }

  if (isLoading || !data) {
    return (
      <Box className="flex flex-col gap-4">
        <PageHeader
          pageTitleComponent={
            <PageBreadcrumb
              items={[
                { label: "Training Programs", to: trainingsHref },
                { label: "…" },
              ]}
            />
          }
        />
        <Box
          className="py-8 px-3"
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const program = data;
  const linkedDocumentDeviceId =
    program.linkedDocument.deviceId ??
    activeGlobalDeviceId ??
    getFallbackDeviceId(devices);
  const linkedDocumentHref = linkedDocumentDeviceId
    ? fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.DEVICE_ID]: linkedDocumentDeviceId,
        [ROUTE_SLUGS.DOC_ID]: program.linkedDocument.id,
      })
    : null;
  const taskGridTemplateColumns = canManage
    ? "minmax(220px, 1.6fr) minmax(140px, 0.9fr) minmax(140px, 0.9fr) minmax(160px, 1fr) 56px"
    : "minmax(220px, 1.6fr) minmax(140px, 0.9fr) minmax(140px, 0.9fr) minmax(160px, 1fr)";

  const fireableRevisions = program.fireableRevisions ?? [];
  const quizByVersionId = new Map(
    (program.quizzes ?? []).map((quiz) => [quiz.documentVersionId, quiz])
  );
  const hasPublishedQuiz = (documentVersionId: string) =>
    !!quizByVersionId.get(documentVersionId)?.publishedAt;
  const revisionsAwaitingFirstOccurrence = fireableRevisions.filter(
    (revision) => !revision.hasOccurrence
  );
  const sendableRevisions = revisionsAwaitingFirstOccurrence.filter(
    (revision) =>
      program.completionMethod !== "QUIZ" ||
      hasPublishedQuiz(revision.documentVersionId)
  );
  const pendingQuizRevisions =
    program.completionMethod === "QUIZ"
      ? revisionsAwaitingFirstOccurrence.filter(
          (revision) => !hasPublishedQuiz(revision.documentVersionId)
        )
      : [];
  const revisionEntryCount =
    sortedOccurrences.length + pendingQuizRevisions.length;
  const hasLiveRevision = fireableRevisions.length > 0;
  const allRevisionsFired =
    fireableRevisions.length > 0 &&
    fireableRevisions.every((revision) => revision.hasOccurrence);

  let triggerDisabledReason: string | null = null;
  if (!program.enabled) {
    triggerDisabledReason = "This training program is disabled.";
  } else if (!hasLiveRevision) {
    triggerDisabledReason =
      "The linked document has no published revision yet.";
  } else if (allRevisionsFired) {
    triggerDisabledReason =
      program.completionMethod === "QUIZ"
        ? "Every live revision has already been sent. The next revision will wait for its quiz to be published."
        : "Every live revision has already been sent. The next revision will be sent automatically when it goes live.";
  } else if (
    program.completionMethod === "QUIZ" &&
    sendableRevisions.length === 0
  ) {
    triggerDisabledReason =
      "Publish a quiz before sending training for this revision.";
  } else if (program.expectedUserCount === 0) {
    triggerDisabledReason =
      "Add at least one trainee or functional group before firing.";
  }

  const triggerDisabled =
    !!triggerDisabledReason || triggerForRevisionMutation.isPending;

  const handleOpenTrigger = () => {
    const firstUnfired = sendableRevisions[0]?.documentVersionId ?? null;
    setPickedRevisionId(firstUnfired);
    setRevisionPickerOpen(true);
  };

  const handleConfirmFireForRevision = async () => {
    if (!pickedRevisionId) return;
    await triggerForRevisionMutation.mutateAsync({
      programId: program.id,
      documentVersionId: pickedRevisionId,
    });
    setRevisionPickerOpen(false);
    setPickedRevisionId(null);
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ programId: program.id });
    setConfirmDelete(false);
    navigate(trainingsHref);
  };

  return (
    <Box className="flex flex-col gap-4">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            items={[
              { label: "Training Programs", to: trainingsHref },
              { label: program.name },
            ]}
          />
        }
        rightButtons={
          canManage ? (
            <Stack direction="row" spacing={1}>
              {canManage && (
                <Tooltip title={triggerDisabledReason ?? ""}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PlayArrowOutlined />}
                      disabled={triggerDisabled}
                      onClick={handleOpenTrigger}
                    >
                      Send training
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlined />}
                component={RouterLink}
                to={trainingEditHref}
              >
                Edit
              </Button>
              {canManage && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              )}
            </Stack>
          ) : undefined
        }
      />

      <Box className="py-2 px-3 flex flex-col gap-4">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 2,
            my: { xs: 0, md: 2 },
          }}
        >
          <MetaBlock label="Linked document">
            {linkedDocumentHref ? (
              <MuiLink
                component={RouterLink}
                to={linkedDocumentHref}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontSize: 14 }}
              >
                {program.linkedDocument.title}
              </MuiLink>
            ) : (
              <Typography variant="body2">
                {program.linkedDocument.title}
              </Typography>
            )}
          </MetaBlock>
          <MetaBlock
            label="Status"
            value={program.enabled ? "Enabled" : "Disabled"}
          />
          <MetaBlock
            label="Created by"
            value={getUserDisplayName(program.creator)}
          />
          <MetaBlock
            label="Time to complete"
            value={
              program.dueAfterDays != null
                ? `${program.dueAfterDays} days`
                : "No due date"
            }
          />
          <MetaBlock
            label="Completion"
            value={
              program.completionMethod === "QUIZ"
                ? "Quiz and sign"
                : "Read and sign"
            }
          >
            {program.completionMethod === "QUIZ" && canManage && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditOutlined />}
                component={RouterLink}
                to={trainingQuizEditHref}
                sx={{ mt: 0.5 }}
              >
                Edit Quiz
              </Button>
            )}
          </MetaBlock>
        </Box>

        <TraineesSection
          assignments={program.assignments}
          expectedUsers={program.expectedUsers}
          canManage={canManage}
          onEdit={() => setPoolEditOpen(true)}
        />

        <Box className="flex flex-col gap-3">
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Overview
          </Typography>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <StatCard
              label="Overall completion"
              value={`${overviewStats.pct}%`}
              barColor="primary"
              barValue={overviewStats.pct}
              caption={
                overviewStats.total === 0
                  ? "No tasks yet"
                  : `${overviewStats.completed} of ${overviewStats.total} done`
              }
            />
            <StatCard
              label="Completed"
              value={`${overviewStats.completed}`}
              secondary={
                overviewStats.total > 0
                  ? ` / ${overviewStats.total}`
                  : undefined
              }
              barColor="success"
              barValue={overviewStats.pct}
            />
            <StatCard
              label="Outstanding"
              value={`${overviewStats.outstanding}`}
              secondary={
                overviewStats.total > 0
                  ? ` / ${overviewStats.total}`
                  : undefined
              }
              barColor="warning"
              barValue={
                overviewStats.total > 0
                  ? (overviewStats.outstanding / overviewStats.total) * 100
                  : 0
              }
            />
          </Stack>
        </Box>

        <Box className="flex flex-col gap-3">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Training on revisions ({revisionEntryCount})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Newest first
            </Typography>
          </Box>

          {pendingQuizRevisions.map((revision) => (
            <Alert
              key={revision.documentVersionId}
              severity="info"
              variant="outlined"
              icon={<InfoOutlinedIcon />}
              action={
                canManage ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      startIcon={<EditOutlined />}
                      component={RouterLink}
                      to={trainingQuizEditHref}
                    >
                      Edit quiz
                    </Button>
                    {quizByVersionId.has(revision.documentVersionId) && (
                      <Tooltip
                        title={
                          publishQuizMutation.isPending
                            ? "Publishing quiz…"
                            : ""
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={publishQuizMutation.isPending}
                            onClick={() =>
                              publishQuizMutation.mutateAsync({
                                programId: program.id,
                                documentVersionId: revision.documentVersionId,
                              })
                            }
                          >
                            Publish
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                ) : undefined
              }
            >
              <Stack spacing={0.25}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {`Publish ${getTrainingRevisionLabel(revision.revision)} Quiz To Send Training`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {quizByVersionId.has(revision.documentVersionId)
                    ? "Quiz draft is ready for review. Publish it to send this revision's training."
                    : "Create and publish a quiz to send this revision's training."}
                </Typography>
              </Stack>
            </Alert>
          ))}

          {revisionEntryCount === 0 ? (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
                px: 2,
                py: 4,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No trainings have been triggered yet.
                {program.enabled
                  ? program.completionMethod === "QUIZ"
                    ? " A new revision will wait for its quiz to be published before training is sent."
                    : " Training will be sent automatically when a new revision goes live."
                  : ""}
              </Typography>
            </Box>
          ) : (
            sortedOccurrences.map((occ, idx) => {
              const expanded = isExpanded(occ.id, idx);
              const tasks = sortedTasksByOccurrenceId[occ.id] ?? occ.tasks;
              const total = tasks.length;
              const done = tasks.filter(
                (t) => t.manualStatus === "DONE"
              ).length;
              return (
                <Box
                  key={occ.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    backgroundColor: "background.paper",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    onClick={() => toggleExpanded(occ.id, idx)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        noWrap
                      >
                        {occurrenceTitle(occ)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Triggered {formatDate(occ.triggeredAt)} · by{" "}
                        {triggererLabel(occ)} ·{" "}
                        {occ.quizId ? "Quiz and sign" : "Read and sign"}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${done}/${total} done`}
                      color={
                        done === total && total > 0 ? "success" : "default"
                      }
                      variant={
                        done === total && total > 0 ? "filled" : "outlined"
                      }
                    />
                    {canManage && (
                      <Tooltip title="Delete occurrence">
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="delete occurrence"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDeleteOccurrence(occ);
                          }}
                        >
                          <DeleteForever fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" aria-label="toggle">
                      {expanded ? (
                        <ExpandLess fontSize="small" />
                      ) : (
                        <ExpandMore fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  <Collapse in={expanded} unmountOnExit>
                    <Box
                      sx={{
                        borderTop: 1,
                        borderColor: "divider",
                        px: 2.5,
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: canManage
                          ? "space-between"
                          : "flex-start",
                        gap: 2,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Trainees ({tasks.length})
                      </Typography>
                      {canManage && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PersonAddAlt1 />}
                          onClick={() => setAddTraineeOccurrence(occ)}
                        >
                          Add Trainee To Revision
                        </Button>
                      )}
                    </Box>
                    {tasks.length === 0 ? (
                      <Box sx={{ px: 2.5, pb: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No users were in scope when this occurrence fired.
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: taskGridTemplateColumns,
                            px: 2.5,
                            py: 1.25,
                            gap: 2,
                            backgroundColor: (t) => t.palette.action.hover,
                            borderTop: 1,
                            borderBottom: 1,
                            borderColor: "divider",
                          }}
                        >
                          <Typography variant="overline" color="text.secondary">
                            Trainee
                          </Typography>
                          <Typography variant="overline" color="text.secondary">
                            Status
                          </Typography>
                          <Typography variant="overline" color="text.secondary">
                            Due date
                          </Typography>
                          <Typography variant="overline" color="text.secondary">
                            Completed
                          </Typography>
                          {canManage && <Box />}
                        </Box>
                        {tasks.map((task, taskIdx) => {
                          const meta = STATUS_STYLE[task.manualStatus];
                          const completedAt = task.trainingSignedAt;
                          const seedKey = task.assignee?.id ?? task.id;
                          return (
                            <Box
                              key={task.id}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: taskGridTemplateColumns,
                                alignItems: "center",
                                px: 2.5,
                                py: 1.5,
                                gap: 2,
                                borderBottom:
                                  taskIdx === tasks.length - 1 ? 0 : 1,
                                borderColor: "divider",
                                cursor: "default",
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                },
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                              >
                                <Avatar
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    bgcolor: colorFromString(seedKey),
                                    color: fgColorFromBg(seedKey),
                                  }}
                                >
                                  {task.assignee
                                    ? initialsFromUser(task.assignee)
                                    : "?"}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                    noWrap
                                  >
                                    {task.assignee
                                      ? getUserDisplayName(task.assignee)
                                      : "—"}
                                  </Typography>
                                  {task.assignee?.email && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      noWrap
                                    >
                                      {task.assignee.email}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                              <Box>
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    px: 1.25,
                                    py: 0.4,
                                    borderRadius: 999,
                                    backgroundColor: meta.bg,
                                    color: meta.fg,
                                    fontSize: 12,
                                    fontWeight: 600,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      backgroundColor: meta.dot,
                                    }}
                                  />
                                  {meta.label}
                                </Box>
                              </Box>
                              <Typography
                                variant="body2"
                                color={
                                  task.dueDate
                                    ? "text.primary"
                                    : "text.secondary"
                                }
                              >
                                {task.dueDate ? formatDate(task.dueDate) : "—"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color={
                                  completedAt
                                    ? "text.primary"
                                    : "text.secondary"
                                }
                              >
                                {completedAt
                                  ? formatDateTime(completedAt)
                                  : "—"}
                              </Typography>
                              {canManage && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Tooltip title="Delete this training task">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      aria-label="delete training task"
                                      onClick={() =>
                                        setConfirmDeleteTask({
                                          occurrenceId: occ.id,
                                          task,
                                        })
                                      }
                                    >
                                      <DeleteOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Collapse>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      <TraineePoolDialog
        open={poolEditOpen}
        onClose={() => setPoolEditOpen(false)}
        orgId={orgId}
        program={program}
      />

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete training</DialogTitle>
        <DialogContent>
          <Typography>
            Delete Training Program: {program.name}?
            <br />
            This will delete all tasks from this training program,{" "}
            <Box component="strong" sx={{ fontWeight: 700 }}>
              including any completed training tasks.
            </Box>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDelete(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={revisionPickerOpen}
        onClose={() => setRevisionPickerOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send training</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {program.completionMethod === "QUIZ"
              ? "Quiz training is sent when each revision's quiz is published. Use this to manually send a published quiz revision — for example, after enabling the program."
              : "This program sends training automatically whenever a new revision goes live. Use this to manually send a specific revision — for example, to catch up after creating the program."}{" "}
            Revisions that already have an occurrence are disabled.
          </Typography>
          {fireableRevisions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              The linked document has no live revisions yet.
            </Typography>
          ) : (
            <TextField
              select
              fullWidth
              size="small"
              label="Revision"
              value={pickedRevisionId ?? ""}
              onChange={(e) => setPickedRevisionId(e.target.value || null)}
              SelectProps={{
                MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
              }}
              sx={{ mt: 1 }}
            >
              {fireableRevisions.map((rev) => {
                const quizUnavailable =
                  program.completionMethod === "QUIZ" &&
                  !hasPublishedQuiz(rev.documentVersionId);
                return (
                  <MenuItem
                    key={rev.documentVersionId}
                    value={rev.documentVersionId}
                    disabled={rev.hasOccurrence || quizUnavailable}
                  >
                    {getTrainingRevisionLabel(rev.revision)}
                    {rev.hasOccurrence
                      ? " — already fired"
                      : quizUnavailable
                        ? " — publish quiz first"
                        : ""}
                  </MenuItem>
                );
              })}
            </TextField>
          )}
          {pickedRevisionId && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              Will fire for {program.expectedUserCount} user
              {program.expectedUserCount === 1 ? "" : "s"}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRevisionPickerOpen(false);
              setPickedRevisionId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmFireForRevision}
            disabled={!pickedRevisionId || triggerForRevisionMutation.isPending}
          >
            Send Trainings
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!confirmDeleteTask}
        onClose={() => setConfirmDeleteTask(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete training task</DialogTitle>
        <DialogContent>
          <Typography>
            Delete the training task for{" "}
            <strong>
              {confirmDeleteTask?.task.assignee
                ? getUserDisplayName(confirmDeleteTask.task.assignee)
                : "this trainee"}
            </strong>
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteTask(null)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!confirmDeleteTask) return;
              await deleteOccurrenceTaskMutation.mutateAsync({
                programId: program.id,
                occurrenceId: confirmDeleteTask.occurrenceId,
                taskStateId: confirmDeleteTask.task.id,
              });
              setConfirmDeleteTask(null);
            }}
            disabled={deleteOccurrenceTaskMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AddTraineeDialog
        open={!!addTraineeOccurrence}
        onClose={() => setAddTraineeOccurrence(null)}
        orgId={orgId}
        programId={program.id}
        occurrence={addTraineeOccurrence}
        existingTaskAssigneeIds={
          addTraineeOccurrence
            ? new Set(
                addTraineeOccurrence.tasks
                  .map((t) => t.assigneeId)
                  .filter((id): id is string => !!id)
              )
            : new Set()
        }
        existingFunctionalGroupIds={
          new Set(
            program.assignments
              .map((a) => a.functionalGroupId)
              .filter((id): id is string => !!id)
          )
        }
        existingDirectUserIds={
          new Set(
            program.assignments
              .map((a) => a.userId)
              .filter((id): id is string => !!id)
          )
        }
      />

      <Dialog
        open={!!confirmDeleteOccurrence}
        onClose={() => setConfirmDeleteOccurrence(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete occurrence</DialogTitle>
        <DialogContent>
          <Typography>
            Delete this occurrence and all{" "}
            {confirmDeleteOccurrence?.tasks.length ?? 0} training task
            {confirmDeleteOccurrence?.tasks.length === 1 ? "" : "s"} it created?
            {confirmDeleteOccurrence?.quizId
              ? " The published revision quiz will be preserved."
              : ""}{" "}
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteOccurrence(null)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!confirmDeleteOccurrence) return;
              await deleteOccurrenceMutation.mutateAsync({
                programId: program.id,
                occurrenceId: confirmDeleteOccurrence.id,
              });
              setConfirmDeleteOccurrence(null);
            }}
            disabled={deleteOccurrenceMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

type MetaBlockProps = {
  label: string;
  value?: string;
  children?: React.ReactNode;
};

const MetaBlock = ({ label, value, children }: MetaBlockProps) => (
  <Box>
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ lineHeight: 1.2 }}
    >
      {label}
    </Typography>
    {value !== undefined && (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {value}
      </Typography>
    )}
    {children && <Box sx={{ mt: 0.5 }}>{children}</Box>}
  </Box>
);

type TraineePoolDialogProps = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  program: TaskProgram;
};

const TraineePoolDialog = ({
  open,
  onClose,
  orgId,
  program,
}: TraineePoolDialogProps) => {
  const { data: orgUsers = [] } = useGetUsersByOrg({ orgId, enabled: open });
  const { data: functionalGroups = [] } = useGetFunctionalGroups(orgId);
  const updateMutation = useUpdateTaskProgram({ orgId });

  const [functionalGroupIds, setFunctionalGroupIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [confirmChanges, setConfirmChanges] = useState(false);

  useEffect(() => {
    if (!open) return;
    setConfirmChanges(false);
    setFunctionalGroupIds(
      program.assignments
        .map((a) => a.functionalGroupId)
        .filter((id): id is string => !!id)
    );
    setUserIds(
      program.assignments
        .map((a) => a.userId)
        .filter((id): id is string => !!id)
    );
  }, [open, program.assignments]);

  const userById = useMemo(
    () => new Map(orgUsers.map((u) => [u.id, u])),
    [orgUsers]
  );
  const fgById = useMemo(
    () => new Map(functionalGroups.map((g) => [g.id, g])),
    [functionalGroups]
  );

  const selectedFunctionalGroups = functionalGroupIds
    .map((id) => fgById.get(id))
    .filter(Boolean) as typeof functionalGroups;
  const selectedUsers = userIds
    .map((id) => userById.get(id))
    .filter(Boolean) as typeof orgUsers;

  const removedFunctionalGroupIds = useMemo(() => {
    const nextIds = new Set(functionalGroupIds);
    return program.assignments
      .map((a) => a.functionalGroupId)
      .filter((id): id is string => !!id && !nextIds.has(id));
  }, [functionalGroupIds, program.assignments]);
  const removedUserIds = useMemo(() => {
    const nextIds = new Set(userIds);
    return program.assignments
      .map((a) => a.userId)
      .filter((id): id is string => !!id && !nextIds.has(id));
  }, [program.assignments, userIds]);
  const addedFunctionalGroupIds = useMemo(() => {
    const currentIds = new Set(
      program.assignments
        .map((a) => a.functionalGroupId)
        .filter((id): id is string => !!id)
    );
    return functionalGroupIds.filter((id) => !currentIds.has(id));
  }, [functionalGroupIds, program.assignments]);
  const addedUserIds = useMemo(() => {
    const currentIds = new Set(
      program.assignments
        .map((a) => a.userId)
        .filter((id): id is string => !!id)
    );
    return userIds.filter((id) => !currentIds.has(id));
  }, [program.assignments, userIds]);
  const hasRemovedAssignments =
    removedFunctionalGroupIds.length > 0 || removedUserIds.length > 0;
  const hasAddedAssignments =
    addedFunctionalGroupIds.length > 0 || addedUserIds.length > 0;
  const hasAssignmentChanges = hasAddedAssignments || hasRemovedAssignments;

  const handleSubmit = async () => {
    if (hasAssignmentChanges && !confirmChanges) {
      setConfirmChanges(true);
      return;
    }

    const assignments: TaskProgramAssignmentInput[] = [
      ...functionalGroupIds.map((functionalGroupId) => ({
        functionalGroupId,
      })),
      ...userIds.map((userId) => ({ userId })),
    ];
    await updateMutation.mutateAsync({
      programId: program.id,
      data: { assignments },
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <span>
            {confirmChanges
              ? "Confirm Trainee Pool Changes"
              : "Edit Trainee Pool"}
          </span>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent className="flex flex-col gap-4 pt-2">
        {confirmChanges ? (
          <>
            {hasAddedAssignments && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  You have added trainees:
                </Typography>
                <Alert severity="info">
                  Newly added trainees will immediately receive a training task
                  if there is a revision.
                </Alert>
              </>
            )}
            {hasRemovedAssignments && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  You have removed trainees:
                </Typography>
                <Alert severity="warning">
                  Trainees no longer in the training pool will have all
                  incomplete trainings deleted.
                </Alert>
              </>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              Choose the functional groups and individual users who should
              receive future training occurrences.
            </Typography>

            <Autocomplete
              multiple
              options={functionalGroups}
              getOptionLabel={(g) => `${g.name} (${g.acronym})`}
              value={selectedFunctionalGroups}
              onChange={(_, value) => {
                setConfirmChanges(false);
                setFunctionalGroupIds(value.map((g) => g.id));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Assigned functional groups" />
              )}
              sx={
                functionalGroupIds.length > 0
                  ? SELECTED_AUTOCOMPLETE_SX
                  : undefined
              }
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />

            <Autocomplete
              multiple
              options={orgUsers}
              getOptionLabel={(u) => getUserDisplayName(u)}
              value={selectedUsers}
              onChange={(_, value) => {
                setConfirmChanges(false);
                setUserIds(value.map((u) => u.id));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Assigned individual users" />
              )}
              sx={userIds.length > 0 ? SELECTED_AUTOCOMPLETE_SX : undefined}
              isOptionEqualToValue={(o, v) => o.id === v.id}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            if (confirmChanges) {
              setConfirmChanges(false);
              return;
            }
            onClose();
          }}
        >
          {confirmChanges ? "Back" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updateMutation.isPending}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type AddTraineeDialogProps = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  programId: string;
  occurrence: TaskProgramOccurrenceDetail | null;
  existingTaskAssigneeIds: Set<string>;
  existingFunctionalGroupIds: Set<string>;
  existingDirectUserIds: Set<string>;
};

const AddTraineeDialog = ({
  open,
  onClose,
  orgId,
  programId,
  occurrence,
  existingTaskAssigneeIds,
  existingFunctionalGroupIds,
  existingDirectUserIds,
}: AddTraineeDialogProps) => {
  const { data: orgUsers = [] } = useGetUsersByOrg({ orgId, enabled: open });
  const { data: functionalGroups = [] } = useGetFunctionalGroups(orgId, {
    enabled: open,
  });
  const addMutation = useAddOccurrenceTrainee({ orgId });

  const [tab, setTab] = useState<"USER" | "GROUP">("USER");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [alsoAddToProgram, setAlsoAddToProgram] = useState(true);

  useEffect(() => {
    if (!open) return;
    setTab("USER");
    setSelectedUserId(null);
    setSelectedGroupId(null);
    setAlsoAddToProgram(true);
  }, [open]);

  const userOptions = useMemo(
    () => orgUsers.filter((u) => !existingTaskAssigneeIds.has(u.id)),
    [orgUsers, existingTaskAssigneeIds]
  );
  const groupOptions = functionalGroups;

  const selectedUser = selectedUserId
    ? (orgUsers.find((u) => u.id === selectedUserId) ?? null)
    : null;
  const selectedGroup = selectedGroupId
    ? (functionalGroups.find((g) => g.id === selectedGroupId) ?? null)
    : null;

  const alreadyInProgram =
    tab === "USER"
      ? selectedUserId
        ? existingDirectUserIds.has(selectedUserId)
        : false
      : selectedGroupId
        ? existingFunctionalGroupIds.has(selectedGroupId)
        : false;

  const submitDisabled =
    addMutation.isPending ||
    (tab === "USER" ? !selectedUserId : !selectedGroupId);

  const handleSubmit = async () => {
    if (!occurrence) return;
    const payload =
      tab === "USER"
        ? {
            userId: selectedUserId,
            alsoAddToProgram: alsoAddToProgram && !alreadyInProgram,
          }
        : {
            functionalGroupId: selectedGroupId,
            alsoAddToProgram: alsoAddToProgram && !alreadyInProgram,
          };
    await addMutation.mutateAsync({
      programId,
      occurrenceId: occurrence.id,
      data: payload,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <span>Add trainee to occurrence</span>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent className="flex flex-col gap-3 pt-2">
        <Typography variant="body2" color="text.secondary">
          Spawns a new training task for the selected user, or for every member
          of the selected functional group, on this occurrence.
        </Typography>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab value="USER" label="User" />
          <Tab value="GROUP" label="Functional group" />
        </Tabs>

        {tab === "USER" && (
          <Autocomplete
            options={userOptions}
            getOptionLabel={(u) => getUserDisplayName(u)}
            value={selectedUser}
            onChange={(_, value) => setSelectedUserId(value?.id ?? null)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pick a user"
                helperText={
                  userOptions.length === 0
                    ? "Every user in the org already has a task on this occurrence."
                    : "Users already on this occurrence are hidden."
                }
              />
            )}
          />
        )}
        {tab === "GROUP" && (
          <Autocomplete
            options={groupOptions}
            getOptionLabel={(g) => `${g.name} (${g.acronym})`}
            value={selectedGroup}
            onChange={(_, value) => setSelectedGroupId(value?.id ?? null)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pick a functional group"
                helperText="Every member of the group will get a training task on this occurrence (members already on it are skipped)."
              />
            )}
          />
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={alsoAddToProgram && !alreadyInProgram}
              disabled={alreadyInProgram}
              onChange={(_, v) => setAlsoAddToProgram(v)}
            />
          }
          label={
            alreadyInProgram
              ? "Already part of this training program"
              : "Also add to this training pool (so they receive future occurrences)"
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleSubmit}
          disabled={submitDisabled}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type TraineesSectionProps = {
  assignments: {
    id: string;
    functionalGroupId?: string | null;
    userId?: string | null;
    functionalGroup: { id: string; name: string; acronym: string } | null;
    user: {
      id: string;
      email: string;
      firstName: string | null;
      lastName: string | null;
    } | null;
  }[];
  expectedUsers: TaskProgramExpectedUser[];
  canManage: boolean;
  onEdit: () => void;
};

const TraineesSection = ({
  assignments,
  expectedUsers,
  canManage,
  onEdit,
}: TraineesSectionProps) => {
  const fgAssignments = assignments.filter((a) => a.functionalGroup);
  const userAssignments = assignments.filter((a) => a.user);
  const traineeGridTemplateColumns =
    "minmax(220px, 1.6fr) minmax(160px, 1fr) minmax(120px, 0.6fr)";

  return (
    <Box className="flex flex-col gap-3">
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          backgroundColor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            px: 2.5,
            py: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="baseline">
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Trainee Pool
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {expectedUsers.length} user
              {expectedUsers.length === 1 ? "" : "s"} will receive the next
              training
            </Typography>
            <Tooltip
              arrow
              title="The list of users who will receive the next triggered training for a revision."
            >
              <IconButton
                aria-label="Training management permission details"
                size="small"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {canManage && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlined />}
              onClick={onEdit}
            >
              Edit Trainee Pool
            </Button>
          )}
        </Box>

        {(fgAssignments.length > 0 || userAssignments.length > 0) && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ px: 2.5, pb: 2 }}
            flexWrap="wrap"
            useFlexGap
            alignItems="flex-start"
          >
            {fgAssignments.length > 0 && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.2 }}
                >
                  From functional groups
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 0.5 }}
                >
                  {fgAssignments.map((a) => (
                    <Chip
                      key={a.id}
                      size="small"
                      label={`${a.functionalGroup!.name} (${a.functionalGroup!.acronym})`}
                    />
                  ))}
                </Stack>
              </Box>
            )}
            {userAssignments.length > 0 && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.2 }}
                >
                  Directly assigned
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 0.5 }}
                >
                  {userAssignments.map((a) => (
                    <Chip
                      key={a.id}
                      size="small"
                      variant="outlined"
                      label={getUserDisplayName(a.user!)}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: traineeGridTemplateColumns,
            px: 2.5,
            py: 1.25,
            gap: 2,
            backgroundColor: (t) => t.palette.action.hover,
            borderTop: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="overline" color="text.secondary">
            Trainee
          </Typography>
          <Typography variant="overline" color="text.secondary">
            Functional groups
          </Typography>
          <Typography variant="overline" color="text.secondary">
            Assignment Source
          </Typography>
        </Box>
        {expectedUsers.length === 0 ? (
          <Box
            sx={{
              px: 2.5,
              py: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No users are currently in scope.
            </Typography>
          </Box>
        ) : (
          expectedUsers.map((u, idx) => {
            const seedKey = u.id;
            return (
              <Box
                key={u.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: traineeGridTemplateColumns,
                  alignItems: "center",
                  px: 2.5,
                  py: 1.5,
                  gap: 2,
                  borderBottom: idx === expectedUsers.length - 1 ? 0 : 1,
                  borderColor: "divider",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: 13,
                      fontWeight: 700,
                      bgcolor: colorFromString(seedKey),
                      color: fgColorFromBg(seedKey),
                    }}
                  >
                    {initialsFromUser(u)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {getUserDisplayName(u)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {u.email}
                    </Typography>
                  </Box>
                </Stack>
                <Box>
                  {u.functionalGroups.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  ) : (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {u.functionalGroups.map((g) => (
                        <Chip
                          key={g.id}
                          size="small"
                          label={g.acronym}
                          title={g.name}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
                <Box>
                  {u.directlyAssigned ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      label="Direct"
                    />
                  ) : (
                    <Chip size="small" variant="outlined" label="Group" />
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};
